/**
 * Subscription revenue and the platform price, the owner's desk (Q9-Q13).
 *
 * SUPERADMIN only. The price is one value for everyone (Q9), so it is a single
 * field here rather than a column on every clinic, and changing it applies to
 * NEW enrolments only, which the page says out loud because the alternative
 * (silently re-pricing running cycles) is what people fear when they touch a
 * price field.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  LoadingRows,
  PageHeader,
  Pill,
  StatCard,
  Table,
  Td,
  Th,
  inputClass,
} from '../components/ui';
import { IconAlert, IconAnalytics, IconCheck, IconPatients, IconReports } from '../components/icons';

type SubStatus = 'ACTIVE' | 'COMPLETED' | 'CANCELED' | 'PAST_DUE';

interface Settings {
  id: string;
  subscriptionPriceCents: number;
  currency: string;
  cycleMonths: number;
  renewalPromptMonth: number;
  billingWeekEndsOnIsoDay: number;
  updatedAt: string;
}

interface Subscription {
  id: string;
  tenantId: string;
  patientId: string;
  status: SubStatus;
  priceCents: number;
  currency: string;
  cycleMonths: number;
  installmentsPaid: number;
  enrolledAt: string;
  billingAnchorAt: string;
  nextChargeAt: string | null;
  renewalPromptedAt: string | null;
  renewalConfirmedAt: string | null;
  tenant: { id: string; name: string; slug: string };
  enrolledBy: {
    id: string;
    name: string;
    email: string;
    cardBrand: string | null;
    cardLast4: string | null;
    enrolmentPausedAt: string | null;
  };
}

interface SubsResponse {
  subscriptions: Subscription[];
  counts: Partial<Record<SubStatus, number>>;
  revenue: { paidCents: number; paidCount: number; failedCents: number; failedCount: number };
}

const SUB_STYLE: Record<SubStatus, string> = {
  ACTIVE: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  COMPLETED: 'bg-ink/5 text-ink-muted border border-ink/10',
  CANCELED: 'bg-ink/5 text-ink-muted border border-ink/10',
  PAST_DUE: 'bg-red-50 text-red-700 border border-red-200',
};

const WEEKDAYS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' },
];

function money(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format((cents ?? 0) / 100);
}

const day = (v: string | null) =>
  v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

export function PlatformSubscriptionsPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<'' | SubStatus>('');

  const settings = useQuery({
    queryKey: ['platform', 'settings'],
    queryFn: () => api.get<{ settings: Settings }>('/platform/settings'),
  });
  const subs = useQuery({
    queryKey: ['platform', 'subscriptions', status],
    queryFn: () =>
      api.get<SubsResponse>(`/platform/subscriptions${qs({ status: status || undefined })}`),
  });

  const runBilling = useMutation({
    mutationFn: () => api.post<{ result: Record<string, number> }>('/platform/billing-run', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform', 'subscriptions'] });
    },
  });

  const counts = subs.data?.counts ?? {};
  const revenue = subs.data?.revenue;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Subscriptions"
        subtitle="Patient monitoring cycles, installment revenue, and the platform price, across every practice."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Active cycles"
          value={String(counts.ACTIVE ?? 0)}
          icon={<IconPatients size={16} />}
        />
        <StatCard
          label="Collected"
          value={revenue ? money(revenue.paidCents) : '-'}
          deltaLabel={revenue ? `${revenue.paidCount} installments` : undefined}
          icon={<IconAnalytics size={16} />}
        />
        <StatCard
          label="Failed"
          value={revenue ? money(revenue.failedCents) : '-'}
          deltaLabel={revenue ? `${revenue.failedCount} installments` : undefined}
          icon={<IconAlert size={16} />}
        />
        <StatCard
          label="Past due cycles"
          value={String(counts.PAST_DUE ?? 0)}
          icon={<IconReports size={16} />}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {settings.isLoading ? (
          <Card title="Plan">
            <LoadingRows rows={3} />
          </Card>
        ) : settings.data ? (
          <SettingsCard settings={settings.data.settings} />
        ) : null}

        <Card
          title="Billing run"
          subtitle="Charges every installment that has fallen due."
        >
          <p className="text-sm text-ink-muted">
            The sweep is safe to run as often as you like, each month can only be charged once, so
            running it twice changes nothing.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button loading={runBilling.isPending} onClick={() => runBilling.mutate()}>
              Run now
            </Button>
            {runBilling.data && (
              <span className="flex items-center gap-1 text-sm text-emerald-700">
                <IconCheck size={14} />
                {runBilling.data.result.charged} charged · {runBilling.data.result.skipped} skipped ·{' '}
                {runBilling.data.result.failed} failed
              </span>
            )}
            {runBilling.error && (
              <span className="text-sm text-red-600">
                {runBilling.error instanceof Error ? runBilling.error.message : 'Run failed.'}
              </span>
            )}
          </div>
          <p className="mt-3 rounded-sm2 border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <strong>This still needs a scheduler in production.</strong> Nothing calls the sweep
            automatically yet, point a daily cron at it, or installments will sit unpaid.
          </p>
        </Card>
      </div>

      <Card padded={false}>
        <div className="flex flex-wrap items-center gap-2 border-b border-ink/5 px-5 py-3">
          {(['', 'ACTIVE', 'PAST_DUE', 'COMPLETED', 'CANCELED'] as const).map((s) => (
            <button
              key={s || 'all'}
              type="button"
              onClick={() => setStatus(s)}
              className={`rounded-sm2 px-2.5 py-1.5 text-xs font-medium transition ${
                status === s
                  ? 'bg-[var(--brand-accent)] text-white'
                  : 'text-ink-muted hover:bg-ink/5'
              }`}
            >
              {s === '' ? 'All' : s.toLowerCase().replace('_', ' ')}
              {s !== '' && counts[s] !== undefined && (
                <span className="ml-1.5 opacity-70">{counts[s]}</span>
              )}
            </button>
          ))}
        </div>

        {subs.isLoading ? (
          <div className="p-5">
            <LoadingRows rows={5} />
          </div>
        ) : subs.isError ? (
          <div className="p-5">
            <ErrorState error={subs.error} retry={() => void subs.refetch()} />
          </div>
        ) : (subs.data?.subscriptions.length ?? 0) === 0 ? (
          <div className="p-5">
            <EmptyState title="No cycles" body="No monitoring cycles match this filter." />
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Practice</Th>
                <Th>Billed to</Th>
                <Th>Status</Th>
                <Th>Progress</Th>
                <Th>Next charge</Th>
              </tr>
            </thead>
            <tbody>
              {subs.data?.subscriptions.map((s) => (
                <tr key={s.id}>
                  <Td>
                    <div className="text-ink">{s.tenant.name}</div>
                    <div className="text-xs text-ink-muted">{s.tenant.slug}</div>
                  </Td>
                  <Td>
                    <div className="text-ink">{s.enrolledBy.name}</div>
                    <div className="text-xs text-ink-muted">
                      {s.enrolledBy.cardLast4
                        ? `${s.enrolledBy.cardBrand ?? 'card'} ···· ${s.enrolledBy.cardLast4}`
                        : 'no card on file'}
                      {s.enrolledBy.enrolmentPausedAt ? ' · paused' : ''}
                    </div>
                  </Td>
                  <Td>
                    <Pill className={SUB_STYLE[s.status]}>
                      {s.status.toLowerCase().replace('_', ' ')}
                    </Pill>
                  </Td>
                  <Td>
                    <span className="tabular-nums text-ink">
                      {s.installmentsPaid} / {s.cycleMonths}
                    </span>
                    <div className="text-[11px] text-ink-muted">
                      {money(s.priceCents, s.currency)}/mo
                    </div>
                  </Td>
                  <Td>{day(s.nextChargeAt)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function SettingsCard({ settings }: { settings: Settings }) {
  const qc = useQueryClient();
  const [price, setPrice] = useState((settings.subscriptionPriceCents / 100).toFixed(2));
  const [cycleMonths, setCycleMonths] = useState(String(settings.cycleMonths));
  const [renewalMonth, setRenewalMonth] = useState(String(settings.renewalPromptMonth));
  const [weekEnds, setWeekEnds] = useState(String(settings.billingWeekEndsOnIsoDay));
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setPrice((settings.subscriptionPriceCents / 100).toFixed(2));
  }, [settings.subscriptionPriceCents]);

  const save = useMutation({
    mutationFn: () =>
      api.patch('/platform/settings', {
        subscriptionPriceCents: Math.round(Number(price) * 100),
        cycleMonths: Number(cycleMonths),
        renewalPromptMonth: Number(renewalMonth),
        billingWeekEndsOnIsoDay: Number(weekEnds),
      }),
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['platform', 'settings'] });
      window.setTimeout(() => setSaved(false), 2500);
    },
  });

  const priceValid = Number(price) >= 1 && Number.isFinite(Number(price));
  const renewalValid = Number(renewalMonth) <= Number(cycleMonths);

  return (
    <Card title="Plan" subtitle="One price for every practice.">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Monthly price per patient"
          error={!priceValid ? 'Must be at least $1.00' : undefined}
          required
        >
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-ink-muted">$</span>
            <input
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className={inputClass}
              inputMode="decimal"
            />
          </div>
        </Field>
        <Field label="Cycle length" hint="Months per monitoring cycle.">
          <input
            value={cycleMonths}
            onChange={(e) => setCycleMonths(e.target.value)}
            className={inputClass}
            inputMode="numeric"
          />
        </Field>
        <Field
          label="Renewal prompt month"
          error={!renewalValid ? 'Cannot fall after the cycle ends' : undefined}
        >
          <input
            value={renewalMonth}
            onChange={(e) => setRenewalMonth(e.target.value)}
            className={inputClass}
            inputMode="numeric"
          />
        </Field>
        <Field label="Billing week closes on" hint="The first charge falls on this day.">
          <select
            value={weekEnds}
            onChange={(e) => setWeekEnds(e.target.value)}
            className={inputClass}
          >
            {WEEKDAYS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          loading={save.isPending}
          disabled={!priceValid || !renewalValid}
          onClick={() => save.mutate()}
        >
          Save plan
        </Button>
        {saved && (
          <span className="flex items-center gap-1 text-sm text-emerald-700">
            <IconCheck size={14} /> Saved
          </span>
        )}
        {save.error && (
          <span className="text-sm text-red-600">
            {save.error instanceof Error ? save.error.message : 'Could not save.'}
          </span>
        )}
      </div>

      <p className="mt-3 text-xs text-ink-muted">
        A price change applies to <strong>new enrolments only</strong>. Cycles already running keep
        the price they were enrolled at, so nobody is re-billed retroactively.
      </p>
    </Card>
  );
}
